// @givmo/employer-module — sign-up VERIFIED / waiting-room screen (/sign-up/verified).
// Ports `sign_up_verified_screen.dart`.
//
// The `pendingManualVerification` waiting room: "Verified!" (green) + "Thank you for verifying your
// work email. A member of our team will reach out to you shortly to complete the onboarding process."
// + the host's sign-out. Auto-advances to the host's landing once the company flips to Active (the Flutter
// `isCompanyVerified()` poll) — we re-resolve the routing state via `refreshRoutingState`; when it
// becomes `authenticated`, route to the landing: the portal's /dashboard, the console's /donations
// (EP-MNT8), where the Donations module opens its onboarding wizard for a new company.

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { testIdProp } from '@givmo/design-system';
import { SignUpHeading, useSignUpHost } from './sign-up-host';

const POLL_INTERVAL_MS = 5000;

export function SignUpVerifiedScreen() {
  const { auth, paths, look, words, Shell } = useSignUpHost();
  const { user, refreshRoutingState, signOut } = auth;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const navigatedRef = useRef(false);
  const landing = paths.dashboard;

  useEffect(() => {
    let cancelled = false;

    const checkOnce = async () => {
      // EP-MNT8-FIX5: signed out, there is no company to ask for. A sign-out made elsewhere remounts
      // this room (the identity boundary, each host's `app.tsx`), and before this guard its first tick
      // sent a signed-out company read ahead of the restart below.
      if (user === null || navigatedRef.current || cancelled) return;
      try {
        const state = await refreshRoutingState();
        if (state === 'authenticated' && !navigatedRef.current && !cancelled) {
          navigatedRef.current = true;
          await navigate({ to: landing });
        }
      } catch {
        // Transient lookup failure — keep waiting; the next tick retries.
      }
    };

    void checkOnce();
    const id = setInterval(() => void checkOnce(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user, refreshRoutingState, navigate, landing]);

  // EP-MNT8-FIX4: a sign-out made ELSEWHERE — another tab, which the SDK reports to this one — left the
  // visitor here with no account, because neither the route nor the screen moves a signed-out visitor,
  // and the poll went on asking for a company it could no longer read until the tab closed. Signed
  // out, the screen restarts the visitor at the host's sign-in door: the request-access screen's
  // restart, to the door rather than the register chooser, since this visitor has an account. Leaving
  // ends the poll: a tick already running finds `navigatedRef` set, and the unmount clears the
  // interval. The identity boundary has cleared the cache on the way (each host's `app.tsx`), and it
  // remounts this screen, so the Sign out below also reaches this restart once React commits it; the
  // two name the same door, so the second navigation changes nothing.
  useEffect(() => {
    if (user !== null || navigatedRef.current) return;
    navigatedRef.current = true;
    void navigate({ to: paths.loginHome, replace: true });
  }, [user, navigate, paths.loginHome]);

  // EP-MNT8-FIX3: Sign out is this screen's only way out, and neither its route nor the screen moves a
  // signed-out visitor, so a bare sign-out left them on "Verified!" on both hosts. It leaves for the
  // host's sign-in door the code screen's way (EP-SUITE2-FIX5): the cache cleared first, because
  // React commits the sign-out a render after `signOut()` resolves and the door's arrival guard still
  // sees this account; replacing, so Back does not return to a waiting room for an account gone. The
  // poll stands down first: a company that turns Active mid-sign-out must not send the visitor to the
  // landing.
  const onSignOut = async () => {
    navigatedRef.current = true;
    await signOut();
    queryClient.clear();
    await navigate({ to: paths.loginHome, replace: true });
  };

  return (
    <Shell testId="page-sign-up-verified">
      <SignUpHeading
        heading={
          <h1 className={look.successHeading} {...testIdProp('auth-verified-heading')}>
            Verified!
          </h1>
        }
      >
        <p className={look.body}>
          Thank you for verifying your work email. A member of our team will reach out to you
          shortly to complete the onboarding process.
        </p>
      </SignUpHeading>
      <button
        type="button"
        onClick={() => void onSignOut()}
        className={look.textButton}
        {...testIdProp('auth-logout')}
      >
        {words.signOut}
      </button>
    </Shell>
  );
}
